import { getQueryClient } from '@eventbrite/legacy-infra.get-create-query-client';
import { ChatbotContextProvider } from '@eventbrite/consumer-search.chatbot';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { useContext, useMemo } from 'react';
import { CompletionType } from '../../api/searchAutocomplete';
import {
    ErrorDisplayProvider,
    LocationInputProvider,
    LocationProvider,
    SearchDropdownContext,
    SearchDropdownProvider,
    SearchInputProvider,
    UserProvider,
} from '../../context';
import { CamelCaseLocation, UserShape } from '../../types';
import { SearchBar } from './SearchBar';

export interface SearchBarContainerProps {
    /**
     * Location object to be used for the search bar
     */
    location?: CamelCaseLocation;
    /**
     * Information about the user and their auth status.
     */
    user?: UserShape;
    id?: string;
    onSelect?: (value: string) => void;
    onChange?: (value: string) => void;
    args?: any[];
    initialQuery?: string;
    children?: React.ReactNode;
    searchButtonClickedExtraHandler?: (e?: React.MouseEvent<HTMLElement>) => void;
    chatbotEnabled?: boolean;
    completionTypes?: CompletionType[];
    usesLanguageSubdirectory?: boolean;
    locale?: string;
    onAutocompleteCallback?: () => void;
    pageType?: string;
}

const client = getQueryClient();

export const SearchBarProviders: React.FunctionComponent<SearchBarContainerProps> = ({
    location,
    user,
    initialQuery,
    children,
    chatbotEnabled,
    usesLanguageSubdirectory,
    locale,
    completionTypes,
    onAutocompleteCallback,
    pageType,
}) => {
    const { initialized } = useContext(SearchDropdownContext);
    const wrappedChildren = useMemo(
        () =>
            initialized ? children : <SearchDropdownProvider>{children}</SearchDropdownProvider>,
        [initialized, children],
    );

    return (
        <QueryClientProvider client={client}>
            <ErrorDisplayProvider>
                <UserProvider user={user}>
                    <LocationProvider location={location}>
                        <LocationInputProvider>
                            <ChatbotContextProvider enabled={chatbotEnabled}>
                                <SearchInputProvider
                                    initialQuery={initialQuery}
                                    usesLanguageSubdirectory={usesLanguageSubdirectory}
                                    locale={locale}
                                    completionTypes={completionTypes}
                                    onAutocompleteCallback={onAutocompleteCallback}
                                    pageType={pageType}
                                >
                                    {wrappedChildren}
                                </SearchInputProvider>
                            </ChatbotContextProvider>
                        </LocationInputProvider>
                    </LocationProvider>
                </UserProvider>
            </ErrorDisplayProvider>
        </QueryClientProvider>
    );
};

export const SearchBarContainerWithProviders: React.FunctionComponent<SearchBarContainerProps> = ({
    location,
    initialQuery,
    searchButtonClickedExtraHandler,
    chatbotEnabled,
    completionTypes,
    onAutocompleteCallback,
    pageType,
}) => (
    <SearchBarProviders
        location={location}
        initialQuery={initialQuery}
        chatbotEnabled={chatbotEnabled}
        completionTypes={completionTypes}
        onAutocompleteCallback={onAutocompleteCallback}
        pageType={pageType}
    >
        <SearchBar searchButtonClickedExtraHandler={searchButtonClickedExtraHandler} />
    </SearchBarProviders>
);
